import { useState } from 'react';
import { X, Lock, Loader2, Flag } from 'lucide-react';
import { createTask, canManageTasks, type CreateTaskInput } from './calendarApi';

const CAL = {
  bg: '#FBFBF7', card: '#FFFFFF', line: '#E7E6DF', ink: '#14140F', muted: '#6B6B60',
  faint: '#9A998C', green: '#2FA037', surf: '#F4F3EC', red: '#EF4444', amber: '#F59E0B',
};
const PRIO: Record<string, { label: string; c: string }> = {
  low: { label: 'Low', c: CAL.faint },
  medium: { label: 'Medium', c: CAL.amber },
  high: { label: 'High', c: CAL.red },
};

export interface UserOption { id: string; firstName: string; lastName: string; }

export interface NewTaskModalProps {
  roleName?: string | null;
  /** current user id — reps self-assign */
  currentUserId?: string;
  users: UserOption[];
  /** prefill scheduled day (yyyy-mm-dd) when opened from a day cell */
  defaultScheduledDate?: string;
  onClose: () => void;
  onCreated: () => void;
}

const Field = ({ label, children, flex }: { label: string; children: React.ReactNode; flex?: number }) => (
  <div style={{ marginBottom: 16, flex }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, color: CAL.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>{label}</div>
    {children}
  </div>
);
const inputStyle: React.CSSProperties = {
  width: '100%', background: CAL.bg, border: `1px solid ${CAL.line}`, borderRadius: 10, padding: '11px 13px', fontSize: 14.5, color: CAL.ink, boxSizing: 'border-box',
};

export function NewTaskModal({ roleName, currentUserId, users, defaultScheduledDate, onClose, onCreated }: NewTaskModalProps) {
  const isManager = canManageTasks(roleName);
  const [title, setTitle] = useState('');
  const [assignedToId, setAssignedToId] = useState(currentUserId ?? '');
  const [priority, setPriority] = useState('medium');
  const [scheduledDate, setScheduledDate] = useState(defaultScheduledDate ?? '');
  const [dueDate, setDueDate] = useState('');
  const [movable, setMovable] = useState(true); // false = non-movable; managers only
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // a non-movable task only makes sense when assigned to someone else (manager-locked work)
  const nonMovable = isManager && !movable;

  async function handleSubmit() {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload: CreateTaskInput = {
        title: title.trim(),
        priority,
        scheduledDate: scheduledDate || undefined,
        dueDate: dueDate || undefined,
      };
      // Only managers may assign-to-others / lock. Reps: backend forces self + movable.
      if (isManager) {
        if (assignedToId) payload.assignedToId = assignedToId;
        payload.movable = !nonMovable;
      }
      await createTask(payload);
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(20,20,15,0.42)' }} />
      <div style={{ position: 'relative', width: 540, maxWidth: 'calc(100vw - 32px)', maxHeight: '90vh', overflowY: 'auto', background: CAL.bg, borderRadius: 20, boxShadow: '0 24px 70px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${CAL.line}`, background: CAL.card, borderRadius: '20px 20px 0 0' }}>
          <div style={{ fontSize: 19, fontWeight: 800, color: CAL.ink, letterSpacing: -0.3 }}>New task</div>
          <button aria-label="Close" onClick={onClose} style={{ width: 32, height: 32, borderRadius: 9, background: CAL.surf, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={18} color={CAL.muted} />
          </button>
        </div>

        <div style={{ padding: '22px 24px' }}>
          {error && <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 10, background: '#FEE2E2', color: '#B91C1C', fontSize: 13, fontWeight: 600 }}>{error}</div>}

          <Field label="Task title">
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Send heat-pump quote to Greenfield" style={inputStyle} />
          </Field>

          <div style={{ display: 'flex', gap: 16 }}>
            {isManager && (
              <Field label="Assign to" flex={1}>
                <select value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)} style={{ ...inputStyle, padding: '10px 11px' }}>
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Priority" flex={1}>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['low', 'medium', 'high'] as const).map((p) => {
                  const on = priority === p;
                  return (
                    <button key={p} onClick={() => setPriority(p)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '10px 0', borderRadius: 9, border: `1px solid ${on ? PRIO[p].c : CAL.line}`, background: on ? '#fff' : CAL.bg, cursor: 'pointer', boxShadow: on ? `0 0 0 1px ${PRIO[p].c}` : 'none' }}>
                      <Flag size={12} color={PRIO[p].c} fill={PRIO[p].c} />
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: on ? PRIO[p].c : CAL.muted }}>{PRIO[p].label}</span>
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Field label="Scheduled day" flex={1}>
              <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Hard deadline · optional" flex={1}>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inputStyle} />
            </Field>
          </div>

          {isManager && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', background: '#EEF1F6', borderRadius: 12 }}>
              <Lock size={19} color="#5B6678" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2B3344' }}>Non-movable</div>
                <div style={{ fontSize: 12, color: '#5B6678' }}>Rep can't postpone — only mark done.</div>
              </div>
              <button
                role="switch"
                aria-checked={nonMovable}
                aria-label="Non-movable"
                onClick={() => setMovable((m) => !m)}
                style={{ width: 46, height: 28, borderRadius: 14, background: nonMovable ? CAL.green : '#C9CDD6', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
              >
                <span style={{ position: 'absolute', top: 3, left: nonMovable ? 21 : 3, width: 22, height: 22, borderRadius: 11, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.15s' }} />
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: `1px solid ${CAL.line}`, background: CAL.card, borderRadius: '0 0 20px 20px' }}>
          <button onClick={onClose} style={{ padding: '11px 20px', borderRadius: 11, border: `1px solid ${CAL.line}`, background: CAL.card, fontSize: 14.5, fontWeight: 700, color: CAL.ink, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !title.trim()} style={{ padding: '11px 24px', borderRadius: 11, border: 'none', background: CAL.green, color: '#fff', fontSize: 14.5, fontWeight: 700, cursor: saving || !title.trim() ? 'default' : 'pointer', opacity: saving || !title.trim() ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving && <Loader2 size={15} className="animate-spin" />}
            {isManager ? 'Assign task' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  );
}
